import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import FollowersIcon from '../../../../../assets/images/followers.svg';
import ImpressionsIcon from '../../../../../assets/images/impressions.svg';
import ReachIcon from '../../../../../assets/images/reach.svg';
import PerformanceBarChart from '../PerformanceBarChart';
import { Profile } from 'types/generated';

const styles = makeStyles()({
  cardTitle: {
    marginBottom: 10,
    fontSize: 16,
    fontWeight: 500
  },
  cardSubtitle: {
    marginBottom: 10,
    fontSize: 14
  },
  insightsContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 10,
    margin: 4
  },
  insightsAmount: {
    fontSize: 18,
    textAlign: 'center'
  },
  insightsChange: {
    fontSize: 12,
    verticalAlign: 'top'
  },
  insightsTitle: {
    fontSize: 14,
    textAlign: 'center'
  },
  insightsSubtitle: {
    color: '#7A7A7A',
    fontSize: 10,
    textAlign: 'center'
  }
});

interface IFacebookPostContent {
  profile: Profile;
}

const FacebookPostContent = ({ profile }: IFacebookPostContent) => {
  const { postStats, pageStats } = profile;

  const { classes } = styles();

  // Build performance data array - always show all three metrics for consistent UI
  // Note: We always include all metrics (defaulting to 0 if data is missing) to maintain UI consistency
  const performanceData: Array<{
    name: string;
    value: number;
    icon: string;
    percentageValue?: number | null | undefined;
  }> = [];

  // Fans: Use page_follows (resolved from page_fans if needed) or fan_count, default to 0
  const fanCount = pageStats?.page_follows ?? profile.fan_count ?? (pageStats as any)?.page_fans ?? 0;
  performanceData.push({
    name: 'Fans',
    value: fanCount,
    icon: FollowersIcon
  });

  // Reach: Always include, default to 0 if postStats or post_media_view is missing
  // This ensures the UI always shows all three metrics consistently
  const reachValue =
    postStats?.post_media_view?.median !== null && postStats?.post_media_view?.median !== undefined
      ? postStats.post_media_view.median
      : 0;
  performanceData.push({
    name: 'Reach',
    value: reachValue,
    icon: ImpressionsIcon
  });

  // Engagement: Always include, default to 0 if postStats or post_activity is missing
  // This ensures the UI always shows all three metrics consistently
  const engagementValue =
    postStats?.post_activity?.median !== null && postStats?.post_activity?.median !== undefined
      ? postStats.post_activity.median
      : 0;
  performanceData.push({
    name: 'Engagement',
    value: engagementValue,
    percentageValue: postStats?.post_activity_percentageOfReach?.median,
    icon: ReachIcon
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box className={'insights-container'}>
          <Typography className={classes.cardTitle}>Post content over last 100 posts</Typography>
          {performanceData.length === 0 ? (
            <Typography>No available data</Typography>
          ) : (
            <PerformanceBarChart data={performanceData} />
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default FacebookPostContent;
